import { useHandleResponse } from '@/utils/useHandleResponse';
import { useMutation } from '@tanstack/react-query';
import { App, Button, Col, Divider, Form, Input, Row } from 'antd';
import { debounce } from 'lodash';
import { FC, useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { updateField, updateProps } from '../../../contextRedux/AccessControlDevice/AccessControlDeviceModel';
import AccessControlDeviceService from '../../../services/accessControlDevice/AccessControlDeviceService';
import { RootState } from '../../../store';
import useWindowSize from '../../../utils/useWindowSize';

const AccessControlEmployerForm: FC = () => {
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const width = useWindowSize();
  const { message } = App.useApp();
  const handleResponse = useHandleResponse();

  // Selectors are memoized for better performance
  const accessControlDevice = useSelector((state: RootState) => state.models.accessControlDevice);
  const initialValues = useMemo(
    () => ({
      id: accessControlDevice?.id,
      name: accessControlDevice?.name,
      cameraReference: accessControlDevice?.cameraReference,
      portReference: accessControlDevice?.portReference,
    }),
    [accessControlDevice]
  );

  // Handle saving with React Query's mutation
  const { mutate: saveAccessControlType } = useMutation({
    mutationFn: (formData: any) => AccessControlDeviceService.upsert(accessControlDevice.id, formData),
    onSuccess: (data: any) => {
      if (data) {
        handleResponse(data, data?.response?.name);
        dispatch(updateProps({ field: 'isModalVisible', value: false }));
        dispatch(updateProps({ field: 'refresh', value: !accessControlDevice.props.refresh }));
      }
    },
    onError: (error: any) => {
      message.error(error.message);
    },
  });

  // Debounced field change handler
  const handleFieldChange = useCallback(
    debounce((changedValues: any) => {
      for (const key in changedValues) {
        dispatch(updateField({ field: key, value: changedValues[key] }));
      }
    }, 300),
    [dispatch]
  );

  // Set form values when `accessControlDevice` updates
  useEffect(() => {
    form.setFieldsValue(accessControlDevice);
  }, [form, accessControlDevice]);

  // Render helper for Form Items
  const renderFormItem = (label: string, name: string, message: string) => (
    <Col span={12} className={`col ${width > 700 ? 'col-wide' : 'col-narrow'}`}>
      <Form.Item label={label} name={name} rules={[{ required: true, message }]}>
        <Input />
      </Form.Item>
    </Col>
  );

  return (
    <Form
      form={form}
      name='accessControlDeviceForm'
      initialValues={initialValues}
      onValuesChange={handleFieldChange}
      onFinish={() => saveAccessControlType(accessControlDevice)}
      layout='vertical'
    >
      <Divider />
      <Row gutter={[16, 16]} className='row'>
        {renderFormItem('Denumire', 'name', 'Introdu denumire!')}
        {renderFormItem('Referinta Camera', 'cameraReference', 'Introdu referinta camera!')}
        {renderFormItem('Referinta Port', 'portReference', 'Introdu referinta port!')}
      </Row>
      <Form.Item>
        <Button type='primary' htmlType='submit'>
          Finalizare
        </Button>
      </Form.Item>
    </Form>
  );
};

export default AccessControlEmployerForm;
